import React, { useMemo } from 'react';
import { Box, Typography, Alert, IconButton, Paper, Chip } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import StandardTable from '../../../common/table/StandardTable';
import StatusChip from '../../../common/StatusChip';
import { operationStatusTone } from '../../../common/statusTones';
import TablePagination from '../UserList/TablePagination';
import { toPersianDigits } from '../../../../utils/convertNumbers';

const formatCurrency = (amount) => toPersianDigits(new Intl.NumberFormat('fa-IR').format(amount || 0));

const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
        return toPersianDigits(new Date(dateString).toLocaleDateString('fa-IR'));
    } catch {
        return '-';
    }
};

// Single-select status filter → API `status` param (comma-separated STATUS_CHOICES codes).
const STATUS_FILTERS = [
    { value: '', label: 'همه' },
    { value: '1,10', label: 'ثبت نام' },
    { value: '2', label: 'حاضر' },
    { value: '3', label: 'غائب' },
    { value: '4', label: 'مرخصی' },
    { value: '5', label: 'کنسل' },
    { value: '6,7,8,9', label: 'موارد ویژه' },
];

export default function OperationsTable({
    operations,
    loading,
    error,
    onDelete,
    showSessionAmount = false,
    // Server-side pagination (0-based page) and status filter, driven by useEnrollmentOperations
    totalCount,
    page = 0,
    rowsPerPage = 15,
    onPageChange,
    onRowsPerPageChange,
    statusFilter = '',
    onStatusFilterChange,
}) {
    const columns = useMemo(() => [
        {
            id: 'date_belonged',
            label: 'تاریخ\nعملیات',
            render: (operation) => (
                <Typography variant="body2">
                    {formatDate(operation.date_belonged)}
                </Typography>
            ),
        },
        {
            id: 'operation_date',
            label: 'تاریخ\nثبت',
            render: (operation) => (
                <Typography variant="body2">
                    {formatDate(operation.operation_date)}
                </Typography>
            ),
        },
        {
            id: 'status', 
            label: 'نوع\nعملیات', 
            render: (operation) => (
                <StatusChip label={operation.status || '-'} tone={operationStatusTone(operation.status)} />
            ) 
        },
        ...(showSessionAmount ? [{
            id: 'session_amount',
            label: 'مبلغ\nجلسه',
            render: (operation) => (
                <Typography variant="body2">
                    {operation.session_amount ? `${formatCurrency(operation.session_amount)} تومان` : '-'}
                </Typography>
            )
        }] : []),
        { 
            id: 'remained', 
            label: 'جلسات\nباقی‌مانده', 
            render: (operation) => (
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {toPersianDigits(operation.remained_sessions || 0)}
                </Typography>
            ) 
        },
        { 
            id: 'payment_method', 
            label: 'روش\nپرداخت', 
            render: (operation) => (
                <Typography variant="body2">
                    {operation.payment_method === 1 ? 'نقدی' :
                     operation.payment_method === 2 ? 'کارت خوان' :
                     operation.payment_method === 3 ? 'انتقال به حساب' :
                     operation.payment_method === 4 ? 'سایر' : 
                     operation.payment_method === null || operation.payment_method === undefined ? '-' : '-'}
                </Typography>
            ) 
        },
        {
            id: 'payment_number',
            label: 'شماره\nپیگیری',
            render: (operation) => (
                <Typography variant="body2">
                    {operation.payment_number ? toPersianDigits(operation.payment_number) : '-'}
                </Typography>
            ) 
        },
        {
            id: 'comment',
            label: 'توضیحات',
            align: 'start',
            render: (operation) => (
                <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {operation.comment || '-'}
                </Typography>
            ) 
        },
        {
            id: 'staff',
            label: 'ثبت‌کننده',
            align: 'start',
            render: (operation) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2">{operation.staff || '-'}</Typography>
                    {operation.created_via === 'assistant' && (
                        <StatusChip tone="info" variant="outlined" label="دستیار هوشمند" />
                    )}
                </Box>
            )
        },
        { 
            id: 'delete', 
            label: 'حذف', 
            compact: true,
            render: (operation) => (
                <IconButton 
                    color="error" 
                    size="small" 
                    onClick={() => onDelete(operation.id)} 
                    aria-label="delete-operation"
                >
                    <DeleteIcon fontSize="small" />
                </IconButton>
            ) 
        },
    ], [onDelete, showSessionAmount]);

    const rows = useMemo(() => (Array.isArray(operations) ? operations : []), [operations]);
    const count = typeof totalCount === 'number' ? totalCount : rows.length;

    return (
        <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'medium' }}>
                تاریخچه عملیات
            </Typography>

            {onStatusFilterChange && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {STATUS_FILTERS.map((item) => (
                        <Chip
                            key={item.value || 'all'}
                            label={item.label}
                            clickable
                            size="small"
                            color={statusFilter === item.value ? 'primary' : 'default'}
                            onClick={() => {
                                if (statusFilter !== item.value) onStatusFilterChange(item.value);
                            }}
                        />
                    ))}
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <StandardTable
                columns={columns}
                rows={rows}
                loading={loading}
                error={error}
                emptyText={statusFilter ? 'موردی با این وضعیت ثبت نشده است' : 'هیچ عملیاتی ثبت نشده است'}
                getRowKey={(r) => r.id}
                ariaLabel="operations"
                minWidth={showSessionAmount ? 900 : 810}
                stickyHeader
            />
            {onPageChange && count > 10 && (
                <Paper variant="outlined" sx={{ mt: 1 }}>
                    <TablePagination
                        totalCount={count}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={onPageChange}
                        onRowsPerPageChange={(e) => onRowsPerPageChange?.(parseInt(e.target.value, 10))}
                        responsive
                    />
                </Paper>
            )}
        </Box>
    );
}

